import React, { useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Typography
} from '@material-ui/core';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  applicationsFetchedSelector,
  applicationsLoadingSelector,
  applicationsSelector,
  fetchCompanyApplications,
  updateApplicationStatus
} from '../../redux/features/applications/applicationsSlice';
import ApplicationCard from './components/ApplicationCard';
import {
  trackApplicationsPageView,
  trackApplicationsStatusDistribution,
  trackCandidateDragStart,
  trackCandidateStatusChanged,
  trackJobApplicationsView
} from '../../helpers/analytics';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    height: 'calc(100vh - 64px)', // Full viewport height minus header
    display: 'flex',
    flexDirection: 'column'
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden'
  },
  cardContent: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  boardContainer: {
    display: 'flex',
    gap: theme.spacing(3),
    overflowX: 'auto',
    paddingBottom: theme.spacing(2),
    flex: 1,
    alignItems: 'stretch'
  },
  column: {
    minWidth: 320,
    maxWidth: 320,
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  columnHeader: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    flexShrink: 0
  },
  columnTitle: {
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  columnBadge: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderRadius: '50%',
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 'bold'
  },
  dropZone: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    transition: 'background-color 0.2s ease',
    minHeight: 200,
    // Custom scrollbar styling
    '&::-webkit-scrollbar': {
      width: 8
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.background.default
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.divider,
      borderRadius: 4,
      '&:hover': {
        backgroundColor: theme.palette.action.hover
      }
    }
  },
  dropZoneDraggingOver: {
    backgroundColor: theme.palette.action.hover
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center'
  },
  emptyColumn: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary
  }
}));

const STATUSES = [
  { id: 'pending', label: 'Applied', color: '#757575' },
  { id: 'reviewed', label: 'Reviewed', color: '#1976d2' },
  { id: 'accepted', label: 'Accepted', color: '#2e7d32' },
  { id: 'rejected', label: 'Rejected', color: '#d32f2f' }
];

const ApplicationsKanbanBoard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { jobId } = useParams(); // Get jobId from URL if present

  const allApplications = useSelector(applicationsSelector);
  const isLoading = useSelector(applicationsLoadingSelector);
  const fetched = useSelector(applicationsFetchedSelector);

  // Filter applications by jobId if provided
  const applications = useMemo(() => {
    if (jobId) {
      return allApplications.filter((app) => app.jobId === jobId);
    }
    return allApplications;
  }, [allApplications, jobId]);

  // Get job title if filtering by specific job
  const jobTitle = useMemo(() => {
    if (jobId && applications.length > 0) {
      return applications[0].jobTitle;
    }
    return null;
  }, [jobId, applications]);

  useEffect(() => {
    if (!fetched && !isLoading) {
      console.log('📋 [Kanban] Fetching company applications');
      dispatch(fetchCompanyApplications());
    }
  }, [dispatch, fetched, isLoading]);

  // Track page view when applications are loaded
  useEffect(() => {
    if (fetched && applications.length > 0) {
      // Track page view
      trackApplicationsPageView(applications.length, jobId || null, 'kanban');

      // If viewing a specific job, track that too
      if (jobId && jobTitle) {
        trackJobApplicationsView(jobId, jobTitle, applications.length);
      }

      // Track status distribution for analytics insights
      const statusCounts = STATUSES.reduce((acc, status) => {
        acc[`${status.id}_count`] = applications.filter(
          (app) => app.status === status.id
        ).length;
        return acc;
      }, {});
      trackApplicationsStatusDistribution(statusCounts);
    }
  }, [fetched, applications, jobId, jobTitle]);

  const handleDragStart = (start) => {
    const { draggableId, source } = start;

    // Find the application being dragged
    const application = applications.find((app) => app.id === draggableId);

    if (application) {
      console.log(`🎯 [Kanban] Drag started for application ${draggableId}`);

      // Track drag start
      trackCandidateDragStart(
        application.id,
        application.candidateName || 'Unknown',
        source.droppableId,
        application.jobId || ''
      );
    }
  };

  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    // Dropped outside the list
    if (!destination) {
      console.log(`🎯 [Kanban] Drag cancelled - dropped outside`);
      return;
    }

    // Dropped in the same place
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      console.log(`🎯 [Kanban] Drag cancelled - same position`);
      return;
    }

    // Get the new status from the destination column
    const newStatus = destination.droppableId;
    const oldStatus = source.droppableId;

    // Find the application being moved
    const application = applications.find((app) => app.id === draggableId);

    console.log(
      `🎯 [Kanban] Moving application ${draggableId} from ${oldStatus} to ${newStatus}`
    );

    if (application) {
      // Track status change
      trackCandidateStatusChanged(
        application.id,
        application.candidateName || 'Unknown',
        oldStatus,
        newStatus,
        application.jobId || ''
      );
    }

    // Update the status via API
    dispatch(updateApplicationStatus(draggableId, newStatus));
  };

  // Group applications by status (memoized for performance)
  const applicationsByStatus = useMemo(() => {
    return STATUSES.reduce((acc, status) => {
      acc[status.id] = applications.filter((app) => app.status === status.id);
      return acc;
    }, {});
  }, [applications]);

  // Performance: With current implementation, we can handle ~500 items smoothly.
  // If you have 1000+ applications, consider implementing react-window for virtualization.
  // The ApplicationCard component is memoized to prevent unnecessary re-renders.

  if (isLoading) {
    return (
      <div className={classes.root}>
        <Card>
          <CardHeader title="Applications" />
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        </Card>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className={classes.root}>
        <Card>
          <CardHeader
            title="Applications"
            subheader="Manage candidate applications with drag-and-drop"
          />
          <Box className={classes.emptyState}>
            <Typography variant="h5" gutterBottom>
              No applications yet
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Applications from candidates will appear here once they start
              applying to your jobs.
            </Typography>
          </Box>
        </Card>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <CardHeader
          title={jobTitle ? `Applications for ${jobTitle}` : 'All Applications'}
          subheader={`Drag and drop candidates to update their status (${
            applications.length
          } ${jobId ? 'for this job' : 'total'})`}
        />
        <Box className={classes.cardContent} p={3}>
          <DragDropContext
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}>
            <div className={classes.boardContainer}>
              {STATUSES.map((status) => (
                <div key={status.id} className={classes.column}>
                  <div className={classes.columnHeader}>
                    <div className={classes.columnTitle}>
                      <Typography variant="h6">{status.label}</Typography>
                      <div
                        className={classes.columnBadge}
                        style={{ backgroundColor: status.color }}>
                        {applicationsByStatus[status.id].length}
                      </div>
                    </div>
                  </div>

                  <Droppable droppableId={status.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`${classes.dropZone} ${
                          snapshot.isDraggingOver
                            ? classes.dropZoneDraggingOver
                            : ''
                        }`}>
                        {applicationsByStatus[status.id].length === 0 ? (
                          <div className={classes.emptyColumn}>
                            <Typography variant="body2">
                              No applications
                            </Typography>
                          </div>
                        ) : (
                          applicationsByStatus[status.id].map(
                            (application, index) => (
                              <ApplicationCard
                                key={application.id}
                                application={application}
                                index={index}
                              />
                            )
                          )
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              ))}
            </div>
          </DragDropContext>
        </Box>
      </Card>
    </div>
  );
};

export default ApplicationsKanbanBoard;
